import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Brain,
  Clock,
  ArrowRight,
  ChevronRight
} from 'lucide-react';

function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0A0B1A]">
      {/* Navigation */}
      <nav className="fixed w-full bg-[#0A0B1A]/95 backdrop-blur-sm z-50 border-b border-[#1E2048]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <LayoutDashboard className="h-8 w-8 text-blue-400" />
              <span className="ml-2 text-2xl font-bold bg-gradient-to-r from-blue-400 to-blue-200 text-transparent bg-clip-text">UnifyAds</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-300 hover:text-blue-400 transition-colors">Features</a>
              <a href="#how-it-works" className="text-gray-300 hover:text-blue-400 transition-colors">How it Works</a>
              <a href="#pricing" className="text-gray-300 hover:text-blue-400 transition-colors">Pricing</a>
              <button 
                onClick={() => navigate('/dashboard')}
                className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-all transform hover:scale-105 shadow-lg hover:shadow-blue-500/25"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,_rgba(120,_80,_255,_0.15)_0%,_transparent_50%)]" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <div className="inline-block mb-4 px-4 py-1 bg-blue-500/10 rounded-full border border-blue-500/20">
              <span className="text-blue-400 text-sm font-semibold">Launching Soon - Join the Waitlist</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 bg-gradient-to-r from-blue-400 via-blue-200 to-purple-400 text-transparent bg-clip-text">
              Streamline Your Ad Campaigns with AdHub
            </h1>
            <p className="text-xl text-gray-400 mb-8 max-w-3xl mx-auto leading-relaxed">
              Manage all your ad platforms in one place with AI-powered insights to optimize performance.
              Save time, increase ROI, and grow your business faster.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button 
                onClick={() => navigate('/dashboard')}
                className="group bg-blue-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-600 transition-all transform hover:scale-105 shadow-lg hover:shadow-blue-500/25 flex items-center justify-center"
              >
                Get Started for Free
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button className="bg-[#21262d] text-gray-300 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#30363d] transition-all transform hover:scale-105 border border-[#30363d] hover:border-[#6e7681]">
                Watch Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,_rgba(120,_80,_255,_0.12)_0%,_transparent_70%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <span className="text-blue-400 font-semibold text-sm uppercase tracking-wider">Features</span>
            <h2 className="text-3xl font-bold text-white mb-4 mt-2">
              Everything You Need to Succeed
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Powerful features to help you manage, optimize, and scale your advertising campaigns
              across all major platforms.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="group p-8 rounded-xl bg-[#151632]/50 backdrop-blur-sm border border-[#1E2048] hover:border-purple-500/25 transition-all duration-300">
              <div className="bg-blue-500/10 rounded-lg p-3 w-fit mb-4 group-hover:scale-110 transition-transform border border-blue-500/20">
                <LayoutDashboard className="h-8 w-8 text-blue-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-white">Centralized Dashboard</h3>
              <p className="text-gray-400">
                View total ad spend and campaign performance across all platforms in one intuitive
                dashboard.
              </p>
            </div>
            <div className="group p-8 rounded-xl bg-[#151632]/50 backdrop-blur-sm border border-[#1E2048] hover:border-purple-500/25 transition-all duration-300">
              <div className="bg-purple-500/10 rounded-lg p-3 w-fit mb-4 group-hover:scale-110 transition-transform border border-purple-500/20">
                <Brain className="h-8 w-8 text-purple-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-white">AI-Powered Recommendations</h3>
              <p className="text-gray-400">
                Get smart suggestions to optimize budgets, improve CTRs, and refine your creative
                strategy.
              </p>
            </div>
            <div className="group p-8 rounded-xl bg-[#151632]/50 backdrop-blur-sm border border-[#1E2048] hover:border-purple-500/25 transition-all duration-300">
              <div className="bg-green-500/10 rounded-lg p-3 w-fit mb-4 group-hover:scale-110 transition-transform border border-green-500/20">
                <Clock className="h-8 w-8 text-green-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-white">Direct Platform Access</h3>
              <p className="text-gray-400">
                Navigate to any ad platform with one click for seamless campaign adjustments.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_0%_0%,_rgba(120,_80,_255,_0.15)_0%,_transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_100%_100%,_rgba(120,_80,_255,_0.15)_0%,_transparent_50%)]" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <span className="text-blue-400 font-semibold text-sm uppercase tracking-wider">Process</span>
            <h2 className="text-3xl font-bold text-white mb-4 mt-2">
              How AdHub Works
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Get started in minutes with our simple three-step process
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Connect Platforms',
                description: 'Easily integrate your advertising accounts with secure OAuth connections',
              },
              {
                step: '02',
                title: 'View Analytics',
                description: 'Access comprehensive performance data and insights in real-time',
              },
              {
                step: '03',
                title: 'Take Action',
                description: 'Optimize campaigns with AI-powered recommendations and quick actions',
              },
            ].map((item) => (
              <div key={item.step} className="relative">
                <div className="bg-[#151632]/50 backdrop-blur-sm p-8 rounded-xl border border-[#1E2048] hover:border-purple-500/25 transition-all duration-300">
                  <div className="text-4xl font-bold text-blue-400 mb-4">{item.step}</div>
                  <h3 className="text-xl font-semibold mb-2 text-white">{item.title}</h3>
                  <p className="text-gray-400">{item.description}</p>
                </div>
                {item.step !== '03' && (
                  <ChevronRight className="hidden md:block absolute top-1/2 -right-6 transform -translate-y-1/2 text-purple-500/25 h-8 w-8" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_100%_0%,_rgba(120,_80,_255,_0.12)_0%,_transparent_70%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_0%_100%,_rgba(120,_80,_255,_0.12)_0%,_transparent_70%)]" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <span className="text-blue-400 font-semibold text-sm uppercase tracking-wider">Pricing</span>
            <h2 className="text-3xl font-bold text-white mb-4 mt-2">
              Simple, Transparent Pricing
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Choose the plan that best fits your needs. All plans include core features.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Starter',
                price: '$49',
                description: 'Perfect for small businesses just getting started with ad management',
                features: [
                  'Up to 3 ad platforms',
                  'Basic analytics',
                  'Email support',
                  '5 team members',
                  'Weekly reports'
                ]
              },
              {
                name: 'Professional',
                price: '$99',
                description: 'Ideal for growing businesses with multiple ad campaigns',
                features: [
                  'Up to 10 ad platforms',
                  'Advanced analytics',
                  'Priority support',
                  '15 team members',
                  'Daily reports',
                  'AI recommendations'
                ]
              },
              {
                name: 'Enterprise',
                price: 'Custom',
                description: 'For large organizations needing advanced features and support',
                features: [
                  'Unlimited ad platforms',
                  'Custom analytics',
                  '24/7 dedicated support',
                  'Unlimited team members',
                  'Real-time reports',
                  'Advanced AI features',
                  'API access'
                ]
              }
            ].map((plan) => (
              <div key={plan.name} className="relative group">
                <div className="h-full bg-[#151632]/50 backdrop-blur-sm p-8 rounded-xl border border-[#1E2048] hover:border-purple-500/25 transition-all duration-300 flex flex-col">
                  <div className="flex-grow">
                    <h3 className="text-xl font-semibold text-white mb-2">{plan.name}</h3>
                    <div className="mb-4">
                      <span className="text-4xl font-bold text-white">{plan.price}</span>
                      {plan.price !== 'Custom' && <span className="text-gray-400">/month</span>}
                    </div>
                    <p className="text-gray-400 mb-6">{plan.description}</p>
                    <ul className="space-y-3 mb-8">
                      {plan.features.map((feature) => (
                        <li key={feature} className="flex items-center text-gray-300">
                          <svg className="h-5 w-5 text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <button 
                    onClick={() => navigate('/dashboard')}
                    className="w-full py-3 px-6 rounded-lg font-semibold transition-all transform hover:scale-105 bg-blue-500 text-white hover:bg-blue-600 shadow-lg hover:shadow-blue-500/25"
                  >
                    {plan.price === 'Custom' ? 'Contact Sales' : 'Get Started'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_100%_0%,_rgba(120,_80,_255,_0.12)_0%,_transparent_70%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_0%_100%,_rgba(120,_80,_255,_0.12)_0%,_transparent_70%)]" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <span className="text-blue-400 font-semibold text-sm uppercase tracking-wider">Testimonials</span>
            <h2 className="text-3xl font-bold text-white mb-4 mt-2">
              Trusted by Marketing Leaders
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              See what our customers have to say about AdHub
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Sarah Johnson',
                role: 'Marketing Director',
                company: 'TechCorp',
                quote: 'AdHub has revolutionized how we manage our ad campaigns. We\'ve seen a 40% increase in ROI since switching.',
                image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=150&h=150',
              },
              {
                name: 'Michael Chen',
                role: 'E-commerce Manager',
                company: 'StyleBox',
                quote: 'The AI recommendations have been game-changing for our business. We\'re saving time and getting better results.',
                image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=150&h=150',
              },
              {
                name: 'Emma Williams',
                role: 'Digital Strategist',
                company: 'GrowthLabs',
                quote: 'Finally, a platform that brings all our ad data together in one place. The insights are invaluable.',
                image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=150&h=150',
              },
            ].map((testimonial) => (
              <div key={testimonial.name} className="group bg-[#151632]/50 backdrop-blur-sm p-8 rounded-xl hover:bg-[#1A1B38]/50 transition-all duration-300 border border-[#1E2048] hover:border-purple-500/25">
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-16 h-16 rounded-full mb-4 ring-4 ring-[#30363d] group-hover:ring-[#6e7681] transition-all"
                />
                <p className="text-gray-300 mb-4">"{testimonial.quote}"</p>
                <div>
                  <p className="font-semibold text-white">{testimonial.name}</p>
                  <p className="text-gray-400">{testimonial.role}</p>
                  <p className="text-gray-400">{testimonial.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_rgba(120,_80,_255,_0.15)_0%,_transparent_70%)]" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Transform Your Ad Management?
          </h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Join thousands of marketers who are saving time and improving results with AdHub
          </p>
          <button 
            onClick={() => navigate('/dashboard')}
            className="bg-blue-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-600 transition-all transform hover:scale-105 shadow-lg hover:shadow-blue-500/25"
          >
            Start Your Free Trial
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,_rgba(120,_80,_255,_0.08)_0%,_transparent_70%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <LayoutDashboard className="h-8 w-8 text-blue-400" />
                <span className="ml-2 text-xl font-bold bg-gradient-to-r from-blue-400 to-blue-200 text-transparent bg-clip-text">AdHub</span>
              </div>
              <p className="text-sm text-gray-400">
                The all-in-one platform for managing and optimizing your ad campaigns.
              </p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-2">
                <li><a href="#features" className="text-gray-400 hover:text-blue-400 transition-colors">Features</a></li>
                <li><a href="#pricing" className="text-gray-400 hover:text-blue-400 transition-colors">Pricing</a></li>
                <li><a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">Integration</a></li>
                <li><a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">API</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">About</a></li>
                <li><a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">Blog</a></li>
                <li><a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">Careers</a></li>
                <li><a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/privacy-policy" className="text-gray-400 hover:text-blue-400 transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms-of-service" className="text-gray-400 hover:text-blue-400 transition-colors">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/data-deletion" className="text-gray-400 hover:text-blue-400 transition-colors">
                    Data Deletion
                  </Link>
                </li>
                <li>
                  <Link to="/cookie-policy" className="text-gray-400 hover:text-blue-400 transition-colors">
                    Cookie Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-[#30363d] mt-12 pt-8 text-center">
            <p className="text-gray-400">© 2024 AdHub. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;